import { format } from "date-fns";
import { useState, useMemo } from "react";
import { useMySwapRequests, useCancelSwap } from "@/hooks/schedule";
import { SwapStatusBadge } from "./SwapStatusBadge";
import { SwapStatusFilter, getStatusFilterArray, calculateSwapCounts } from "./SwapStatusFilter";
import type { SwapStatus } from "./SwapStatusBadge";
import type { SwapFilterStatus } from "./SwapStatusFilter";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Users, MessageSquare, X, Loader2 } from "lucide-react";

interface SwapRequestsListProps {
  className?: string;
  currentWeek?: Date; // NEW: Add currentWeek prop for cache invalidation
}

export function SwapRequestsList({ className, currentWeek }: SwapRequestsListProps) {
  const [statusFilter, setStatusFilter] = useState<SwapFilterStatus>('pending');
  
  // Get all data for counts calculation
  const { data: allSwapRequests = [], isLoading: isLoadingAll } = useMySwapRequests();
  
  // Get filtered data for display
  const statusFilterArray = getStatusFilterArray(statusFilter);
  const { data: filteredSwapRequests = [], isLoading: isLoadingFiltered } = useMySwapRequests(statusFilterArray);
  
  // Calculate counts for filter badges
  const counts = useMemo(() => calculateSwapCounts(allSwapRequests), [allSwapRequests]);
  
  // Use filtered data for display, all data loading state for initial load
  const swapRequests = filteredSwapRequests;
  const isLoading = isLoadingAll || isLoadingFiltered;
  const cancelSwap = useCancelSwap(currentWeek); // FIXED: Pass currentWeek for proper cache invalidation

  const formatShiftTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  const formatShiftDate = (startTime: string) => {
    return format(new Date(startTime), 'EEEE, MMM d');
  };

  const handleCancelSwap = async (swapId: string) => {
    try {
      await cancelSwap.mutateAsync({ swapId });
    } catch (error) {
      console.error('Error cancelling swap:', error);
    }
  };

  const canCancelSwap = (status: string) => {
    return status === 'open' || status === 'accepted' || status === 'manager_pending';
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">My Swap Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading swap requests...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Context-aware empty state message
  const getEmptyStateMessage = () => {
    switch (statusFilter) {
      case 'pending':
        return {
          title: "No pending swap requests",
          subtitle: "Create a swap request from your shifts to get started."
        };
      case 'completed':
        return {
          title: "No completed swaps",
          subtitle: "Completed swaps will appear here once they're finalized."
        };
      case 'cancelled':
        return {
          title: "No cancelled requests",
          subtitle: "Cancelled or declined swap requests will appear here."
        };
      case 'all':
        return {
          title: "No swap requests yet",
          subtitle: "Create a swap request from your shifts to get started."
        };
      default:
        return {
          title: "No swap requests",
          subtitle: "Create a swap request from your shifts to get started."
        };
    }
  };

  if (swapRequests.length === 0) {
    const emptyState = getEmptyStateMessage();
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            My Swap Requests
            <Badge variant="secondary" className="text-xs">
              {counts.all}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Component */}
          <SwapStatusFilter
            selectedStatus={statusFilter}
            onStatusChange={setStatusFilter}
            counts={counts}
          />
          
          {/* Empty State */}
          <div className="text-center py-8">
            <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {emptyState.title}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {emptyState.subtitle}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          My Swap Requests
          <Badge variant="secondary" className="text-xs">
            {counts.all}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter Component */}
        <SwapStatusFilter
          selectedStatus={statusFilter}
          onStatusChange={setStatusFilter}
          counts={counts}
        />
        <div className="h-[400px] overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-3">
              {swapRequests.map((request) => (
              <Card key={request.swap_id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Header with status and actions */}
                    <div className="flex items-center justify-between">
                      <SwapStatusBadge status={request.status as SwapStatus} />
                      {canCancelSwap(request.status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelSwap(request.swap_id)}
                          disabled={cancelSwap.isPending}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Cancel swap request</span>
                        </Button>
                      )}
                    </div>

                    {/* Shift details */}
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        Your shift to swap:
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {formatShiftDate(request.requester_shift_start)}
                        </span>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatShiftTime(request.requester_shift_start, request.requester_shift_end)}
                        </div>
                      </div>
                    </div>

                    {/* Message if provided */}
                    {request.message && (
                      <div className="bg-muted/50 rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-foreground">{request.message}</p>
                        </div>
                      </div>
                    )}

                    {/* Candidates info */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>
                          {request.candidate_count} candidate{request.candidate_count === 1 ? '' : 's'} invited
                        </span>
                      </div>
                      <span>
                        Created {format(new Date(request.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>

                    {/* Accepted candidate info */}
                    {request.status === 'accepted' && request.accepted_candidate_name && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <p className="text-sm text-green-800">
                          <strong>{request.accepted_candidate_name}</strong> accepted your swap request
                        </p>
                      </div>
                    )}

                    {/* Manager pending info */}
                    {request.status === 'manager_pending' && (
                      <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                        <p className="text-sm text-purple-800">
                          Waiting for manager approval
                        </p>
                      </div>
                    )}

                    {/* Finalized info */}
                    {request.status === 'finalized' && request.accepted_candidate_name && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <p className="text-sm text-green-800">
                          Swap completed with <strong>{request.accepted_candidate_name}</strong>
                        </p>
                      </div>
                    )}

                    {/* Expired info */}
                    {request.status === 'expired' && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                        <p className="text-sm text-gray-600">
                          This swap request has expired
                        </p>
                      </div>
                    )}

                    {/* Cancelled info */}
                    {request.status === 'cancelled' && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                        <p className="text-sm text-gray-600">
                          This swap request was cancelled
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              ))}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}
